// web/fog.ts — FOG OF WAR / exploration memory (presentation + persisted blob).
//
// On the big default world (150x88) the player should DISCOVER the ecosystem, not
// see it all at once. This tracks two per-tile masks over the grid:
//   • `explored`  — sticky: any tile a player unit has ever sensed (persists in the
//                   save; the player "remembers the ground" forever);
//   • `visibleNow`— recomputed every reveal(): the tiles currently in a living
//                   player unit's vision this frame (drives "live vs. remembered"
//                   rendering — you see enemies only where you can currently sense).
//
// This is render/UI state ONLY: it is never folded into any sim state hash and never
// affects determinism or replay. It is persisted as a compact RLE blob alongside the
// save's other non-replayed data (history), and a dims-mismatch decode degrades to a
// fresh empty map rather than crashing.
//
// Reveal walks only LIVING player-colony organisms via the SoA active set, so cost
// scales with the player's unit count, not the map area.

import { KIND, CASTE } from '../src/types.ts';
import { PLAYER_COLONY } from './game.ts';
import type { GameSession } from './game.ts';

/** Base Chebyshev vision radius (tiles) for a ground unit. */
export const VISION_BASE = 7;
/** Flyers scout: a wider vision radius so a Flyer sweep reveals more ground. */
export const VISION_FLYER = 12;

/** The serialized exploration blob carried in the save (compact, non-replayed). */
export interface ExplorationSave {
  w: number;
  h: number;
  /** Run-length encoding of the explored mask: alternating run lengths, starting with a run of 0s. */
  rle: number[];
}

/**
 * Visit every tile within Chebyshev radius `r` of (cx,cy), clamped to the grid.
 * Pure + allocation-free (the callback does the work). Exported for testing the
 * reveal footprint independently of a session.
 */
export function tilesInRadius(
  cx: number, cy: number, r: number, w: number, h: number,
  cb: (x: number, y: number) => void,
): void {
  if (r < 0) return;
  let y0 = cy - r; if (y0 < 0) y0 = 0;
  let y1 = cy + r; if (y1 > h - 1) y1 = h - 1;
  let x0 = cx - r; if (x0 < 0) x0 = 0;
  let x1 = cx + r; if (x1 > w - 1) x1 = w - 1;
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) cb(x, y);
  }
}

/** Vision radius (tiles) for a caste — Flyers see farther (they scout). */
export function visionForCaste(caste: number): number {
  return caste === CASTE.Flyer ? VISION_FLYER : VISION_BASE;
}

export class ExplorationMap {
  readonly width: number;
  readonly height: number;
  /** 0 = never seen, 1 = explored (sticky). Persisted. */
  readonly explored: Uint8Array;
  /** 0/1 — tiles a living player unit can sense THIS frame (recomputed each reveal()). */
  readonly visibleNow: Uint8Array;
  /** Render-only "remembered food": tile index → last-seen food amount. NOT saved or
   *  hashed — it rebuilds as the player explores. The renderer notes currently-visible
   *  food each frame, forgets a tile when a revisit finds it empty, and draws faint
   *  markers for remembered food now hidden under fog. */
  readonly remFood = new Map<number, number>();
  private exploredTotal = 0;
  /** Sim tick of the last reveal() that ran — lets a same-tick call (paused, or a render
   *  frame between sim steps) skip the recompute. -1 ⇒ never revealed. */
  private lastRevealTick = -1;

  constructor(width: number, height: number) {
    this.width = Math.max(0, width | 0);
    this.height = Math.max(0, height | 0);
    const n = this.width * this.height;
    this.explored = new Uint8Array(n);
    this.visibleNow = new Uint8Array(n);
  }

  private idx(x: number, y: number): number {
    return y * this.width + x;
  }

  /** Set the maintained explored total (used by fromSave after a bulk decode). */
  private setExploredTotal(total: number): void {
    this.exploredTotal = total;
  }

  /**
   * Recompute visibility from the LIVING player-colony organisms. Clears the
   * current-frame visible mask, then for each unit marks every tile within its
   * vision radius (Chebyshev) as visible-now and (stickily) explored. Cost is
   * O(players · radius²), not map area, and allocation-free per call.
   */
  reveal(session: GameSession): void {
    // Change-gate: unit positions only change when the sim advances. A same-tick reveal
    // (paused, or a render frame between sim steps) recomputes an identical mask, so skip
    // it. Guarded to a numeric tick so headless test stubs (no `tick`) always run.
    const tick = session.tick;
    if (typeof tick === 'number') {
      if (tick === this.lastRevealTick) return;
      this.lastRevealTick = tick;
    }
    const e = session.world.entities;
    const active = e.activeView();
    const n = e.aliveCount;
    const W = this.width;
    const H = this.height;
    const explored = this.explored;
    const visible = this.visibleNow;
    visible.fill(0);
    let total = this.exploredTotal;
    for (let i = 0; i < n; i++) {
      const id = active[i] as number;
      if ((e.kind[id] as number) !== KIND.Organism) continue;
      if ((e.colony[id] as number) !== PLAYER_COLONY) continue;
      const px = e.posX[id] as number;
      const py = e.posY[id] as number;
      const r = visionForCaste(e.caste[id] as number);
      let y0 = py - r; if (y0 < 0) y0 = 0;
      let y1 = py + r; if (y1 > H - 1) y1 = H - 1;
      let x0 = px - r; if (x0 < 0) x0 = 0;
      let x1 = px + r; if (x1 > W - 1) x1 = W - 1;
      for (let y = y0; y <= y1; y++) {
        let k = y * W + x0;
        for (let x = x0; x <= x1; x++, k++) {
          visible[k] = 1;
          if (explored[k] === 0) { explored[k] = 1; total++; }
        }
      }
    }
    this.exploredTotal = total;
  }

  isExplored(x: number, y: number): boolean {
    if (x < 0 || y < 0 || x >= this.width || y >= this.height) return false;
    return this.explored[this.idx(x, y)] === 1;
  }

  isVisible(x: number, y: number): boolean {
    if (x < 0 || y < 0 || x >= this.width || y >= this.height) return false;
    return this.visibleNow[this.idx(x, y)] === 1;
  }

  /** Note the last-seen food amount at (x,y) — the renderer calls this for each
   *  currently-visible food node (amount > 0 records it; ≤ 0 forgets it). Render-only
   *  memory; never saved or hashed. */
  noteFood(x: number, y: number, amount: number): void {
    if (x < 0 || y < 0 || x >= this.width || y >= this.height) return;
    const k = this.idx(x, y);
    if (amount > 0) this.remFood.set(k, amount);
    else this.remFood.delete(k);
  }

  /** Total explored tiles (O(1); maintained incrementally by reveal()). */
  exploredCount(): number {
    return this.exploredTotal;
  }

  /** Explored fraction of the world as an integer percent (0..100) for the HUD. */
  exploredPct(): number {
    const n = this.width * this.height;
    if (n <= 0) return 0;
    return Math.round((100 * this.exploredTotal) / n);
  }

  /**
   * Encode the explored mask as run-length pairs for the save. Long runs of
   * unexplored/explored ground compress to a handful of integers, so even the full
   * 13,200-tile map stays tiny. The RLE always starts counting 0s (a leading run of
   * 0 is emitted when tile 0 is explored), so decode is unambiguous.
   */
  toSave(): ExplorationSave {
    const rle: number[] = [];
    const explored = this.explored;
    const n = explored.length;
    let cur = 0;        // value of the run we're counting (starts at 0)
    let run = 0;
    for (let i = 0; i < n; i++) {
      const v = explored[i] as number;
      if (v === cur) { run++; }
      else { rle.push(run); cur = v; run = 1; }
    }
    rle.push(run);
    return { w: this.width, h: this.height, rle };
  }

  /**
   * Decode a saved exploration blob into a map for the CURRENT grid. If the blob is
   * missing/malformed, or its dims differ from (width,height), return a FRESH empty
   * map (the world shape changed — discard stale exploration rather than crash or
   * misalign). Never throws.
   */
  static fromSave(data: ExplorationSave | null | undefined, width: number, height: number): ExplorationMap {
    const map = new ExplorationMap(width, height);
    if (!data || typeof data !== 'object') return map;
    if (data.w !== width || data.h !== height || !Array.isArray(data.rle)) return map;
    const explored = map.explored;
    const n = explored.length;
    let i = 0;
    let val = 0;
    let total = 0;
    for (const rawRun of data.rle) {
      const run = rawRun | 0;
      if (run < 0) return new ExplorationMap(width, height); // corrupt → fresh
      if (val === 1) {
        const end = Math.min(n, i + run);
        for (let k = i; k < end; k++) { explored[k] = 1; total++; }
      }
      i += run;
      val ^= 1;
      if (i > n) return new ExplorationMap(width, height); // overran → corrupt → fresh
    }
    // A well-formed RLE sums exactly to n; anything else is corrupt.
    if (i !== n) return new ExplorationMap(width, height);
    map.setExploredTotal(total);
    return map;
  }
}
